'use strict';

export default angular
  .module('maestro.services.resource', [])

  .factory('Resource', function($q) {

    /**
     * Simple Promise-based resource with loading indicator and dependencies
     */
    class Resource {

      /**
       * Current value
       * @type {Object|undefined}
       * @private
       */
      #_value;

      /**
       * Loading indicator
       * @type {boolean}
       * @private
       */
      #_loading = false;

      /**
       * Initialization function
       * @type {function}
       * @private
       */
      #_getFunction;

      /**
       * Cached initialization Promise
       * @type {Promise|undefined}
       * @private
       */
      #_initPromise;

      /**
       * List of Resource dependencies. Resources on which the current resource depends.
       * @type {Array<Resource>}
       * @private
       */
      #_dependencies = [];

      /**
       * List of Resource dependents. Resources that depend on the current resource.
       * @type {Array<Resource>}
       * @private
       */
      dependents = [];

      /**
       * Flag showing that factory is in process of destruction
       */
      #_destroyed = false;

      /**
       * Current value
       * @return {Object|undefined}
       */
      get value() {
        return this.#_value;
      }

      /**
       * Current value
       * @param {Object|undefined} val
       */
      set value(val) {
        this.#_value = val;
      }

      /**
       * Dirty hack for type checking
       * @return {boolean}
       */
      get isResource() {
        return true;
      }

      /**
       * Loading indicator
       * @return {boolean}
       */
      get loading() {
        return this.#_loading;
      }

      /**
       * Simple Promise-based Resource with loading indicator and dependencies
       * @param {function} getFunction initialization function.
       *   It should return the Value or promise of the Value
       * @param {Array<Resource>} [dependencies] list of Resource dependencies as list of Resource objects
       * @param {Object} defaultValue pre-initialized Value
       */
      constructor(getFunction, dependencies, defaultValue) {
        if (!getFunction || !angular.isFunction(getFunction)) {
          throw new Error('Initialization function should be passed to Resource constructor');
        }
        if (dependencies && (!angular.isArray(dependencies) || dependencies.some(d => !d.isResource))) {
          throw new Error('Dependencies should be defined as Array of Resources');
        }
        this.#_getFunction = getFunction;
        this.#_dependencies = (dependencies || []).slice(0);

        this.#_dependencies.forEach(dependency => {
          dependency.dependents.push(this);
        });

        if (defaultValue != null) {
          this.#_value = defaultValue;
        }
      }

      /**
       * Init resource. If dependencies was defined - current resource will wait until they will be loaded
       * @param {boolean} [forceReInit] force re-initialize value
       * @return {Promise}
       */
      init(forceReInit) {
        if (this.#_destroyed) {
          return;
        }
        if (forceReInit !== true && this.#_initPromise) {
          return $q.when(this.#_initPromise)
            .then(() => this.#_value);
        }
        this.#_loading = true;
        this.#_initPromise = $q.all(this.#_dependencies.map(d => d.init()))
          .then(() => {
            if (this.#_destroyed) {
              return;
            }
            try {
              return this.#_getFunction();
            } catch (error) {
              console.error('Unable to initialize resource', this);
              console.error(error);
              return;
            }
          })
          .then((data) => {
            this.#_value = data;
            this.#_loading = false;
            return data;
          }, (err) => {
            this.#_value = undefined;
            this.#_loading = false;
            return $q.reject(err);
          });

        return $q.when(this.#_initPromise);
      }

      /**
       * Drop resource cache
       */
      dropCache() {
        this.#_initPromise = undefined;
      }

      /**
       * Drop current resource cache and caches of all it's dependents and their dependents, etc.
       */
      dropCacheRecursively() {
        this.#_initPromise = undefined;
        this.dependents?.forEach(d => d.dropCacheRecursively());
      }

      forceReInitDependents() {
        let promises = [];
        this.dependents?.forEach(d => {
          promises.push(d.init(true));
        });
        return $q.all(promises);
      }

      forceReInitDependentsRecursively() {
        let promises = [];
        this.dependents?.forEach(d => {
          promises.push(d.init(true)
            .then(() => d.forceReInitDependentsRecursively()));
        });
        return $q.all(promises);
      }

      /**
       * Destructor. Use it to clear memory faster. Yes, it makes sense
       * You will be unable to use this object after calling this function.
       */
      destroy() {
        this.#_initPromise = undefined;
        this.#_dependencies = undefined;
        this.dependents = undefined;
        this.#_getFunction = undefined;
        this.#_loading = false;
        this.#_value = undefined;
        this.#_destroyed = true;
      }
    }

    return Resource;
  })

  .factory('ResourceGroup', function($q) {

    /**
     * Simple Promise-based resource group with loading indicator and dependencies
     */
    class ResourceGroup {

      /**
       * Loading indicator
       * @type {boolean}
       * @private
       */
      #_loading = true;

      /**
       * List of Resource dependencies
       * @type {Array<Resource>}
       * @private
       */
      #_dependencies = [];

      /**
       * List of Resource dependents. Resources that depend on the current resource.
       * @type {Array<Resource>}
       * @private
       */
      dependents = [];

      /**
       * Loading indicator
       * @return {boolean}
       */
      get loading() {
        return this.#_loading;
      }

      /**
       * Dirty hack for type checking
       * @return {boolean}
       */
      get isResource() {
        return true;
      }

      /**
       * Grouping feature for Resource
       * @param {Array<Resource|ResourceGroup>} [dependencies] list of Resource dependencies as list of Resource objects
       */
      constructor(dependencies) {
        if (!dependencies || !angular.isArray(dependencies) || dependencies.some(d => !d.isResource)) {
          throw new Error('Dependencies should be defined as Array of Resources');
        }
        this.#_dependencies = (dependencies || []).slice(0);

        this.#_dependencies.forEach(dependency => {
          dependency.dependents.push(this);
        });
      }

      /**
       * Init resource. If dependencies was defined - current resource will wait until they will be loaded
       * @return {Promise}
       */
      init() {
        this.#_loading = true;
        return $q.all(this.#_dependencies.map(d => d.init()))
          .then((data) => {
            this.#_loading = false;
            return data;
          }, (err) => {
            this.#_loading = false;
            return $q.reject(err);
          });
      }

      /**
       * Drop current resource cache and caches of all it's dependencies and their dependencies, etc.
       */
      dropCacheRecursively() {
        this.dependents?.forEach(d => d.dropCacheRecursively());
      }

      /**
       * Destructor. Use it to clear memory faster. Yes, it makes sense
       * You will be unable to use this object after calling this function.
       */
      destroy() {
        this.#_dependencies = undefined;
        this.dependents = undefined;
        this.#_loading = false;
      }
    }

    return ResourceGroup;
  })

  .name;
